import type { JobSubcategoryKey } from "@/domains/freelance/taxonomy/types";
import type { JobOfferV2Insert } from "../../../types";
import type { RefineState } from "../types";
import {
  type OfferSubcategoryTaxonomy,
  toJobSubcategoryField,
  visibleSubcategories,
} from "./offer-subcategories";

/**
 * The `job_offer` of `POST /job-offer/v2`, built from the refine state. Pure:
 * no `@/api/client`, no Solid, no TanStack, so the node tests can import it.
 *
 * `job_subcategory` follows the subcategories as the review shows them: the
 * chosen keys in order, `[]` for the whole category, and no key at all when
 * not declared. `JobOfferV2Insert` omits the field, hence the intersection.
 */
export function toJobOfferInsert(
  state: RefineState,
  description: string,
  from: unknown,
  taxonomy?: OfferSubcategoryTaxonomy,
): JobOfferV2Insert & { job_subcategory?: JobSubcategoryKey[] } {
  const jobSubcategory = toJobSubcategoryField(
    visibleSubcategories(state.job_category, state.subcategories, taxonomy),
  );

  return {
    title: state.title,
    description,
    remote_policy: state.remote_policy!,
    payment_status: "completed" as const,
    start_date: state.start_date ?? undefined,
    renewable: state.renewable,
    experience_level: state.experience_level ?? undefined,
    skills: state.skills,
    daily_rate_min: state.daily_rate_min ?? undefined,
    daily_rate_max: state.daily_rate_max ?? undefined,
    budget: state.budget ?? undefined,
    currency: state.currency,
    location: state.location ? [state.location] : undefined,
    payment_type: state.payment_type ?? undefined,
    effort: state.effort ?? undefined,
    duration: state.duration ?? undefined,
    job_category: state.job_category!,
    internal_ats: state.internal_ats,
    apply_url: state.apply_url || undefined,
    calendar_url: state.calendar_url || undefined,
    visibility: from === "ai_generation" ? "private" : "public",
    ...(jobSubcategory !== undefined && { job_subcategory: jobSubcategory }),
  };
}
